import axios, { AxiosError, AxiosHeaders } from 'axios';
import { clearAuthToken, getAuthToken } from '@/lib/auth/auth-token';
import {
  isAuthShellRoute,
  isProtectedOnboardingRoute,
} from '@/lib/auth/auth-route';
import { routes } from '@/lib/constants';

interface ErrorResponse {
  response?: string;
  message?: string;
  error?: string | { message?: string };
  errors?: string[] | { message?: string }[];
  detail?: string;
}

const DEFAULT_SERVER_ERROR_MESSAGE =
  'Something went wrong on our end. It’s not you, it’s us. Please try again shortly.';

function getErrorMessage(error: AxiosError<ErrorResponse>): string {
  const status = error.response?.status;
  if (typeof status === 'number' && status >= 500) {
    return DEFAULT_SERVER_ERROR_MESSAGE;
  }
  const data = error.response?.data;
  if (!data) return error.message || 'An error occurred';
  if (typeof data === 'string') return data;
  const err = data.error;
  const msg =
    data.response ??
    data.message ??
    data.detail ??
    (typeof err === 'string' ? err : err?.message) ??
    (Array.isArray(data.errors)
      ? data.errors[0] != null
        ? typeof data.errors[0] === 'string'
          ? data.errors[0]
          : (data.errors[0] as { message?: string }).message
        : undefined
      : undefined);
  return msg ?? error.message ?? 'An error occurred';
}

const apiBaseUrl = `${import.meta.env.VITE_BASE_URL}/v1`;

export const axiosInstance = axios.create({
  baseURL: apiBaseUrl,
  headers: { 'Content-Type': 'application/json' },
});

axiosInstance.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (!token) return config;

  const requestUrl = config.url ?? '';
  const isExternalRequest =
    requestUrl.startsWith('http://') || requestUrl.startsWith('https://');
  const isOurApi =
    !isExternalRequest ||
    requestUrl.startsWith(import.meta.env.VITE_BASE_URL as string);

  if (!isOurApi) return config;

  const headers = AxiosHeaders.from(config.headers);
  headers.set('Authorization', `Bearer ${token}`);
  config.headers = headers;

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ErrorResponse>) => {
    if (error.response?.status === 401) {
      const path = window.location?.pathname ?? '';

      // Keep session during sign-up onboarding (phone OTP → business profile).
      if (!isProtectedOnboardingRoute(path)) {
        clearAuthToken();

        if (!isAuthShellRoute(path)) {
          window.location.replace(routes.login);
        }
      }
    }

    const customError = new Error(getErrorMessage(error));
    return Promise.reject(customError);
  }
);
